import type {
  SubtitleBatchResult,
  SubtitleQueueSummary,
  TagDraft,
  TagMutationResult,
} from "./settings-types";

const numberFormatter = new Intl.NumberFormat("en-US");

const tagColorStyles = [
  "border-sky-200 bg-sky-100 text-sky-800",
  "border-emerald-200 bg-emerald-100 text-emerald-800",
  "border-amber-200 bg-amber-100 text-amber-800",
  "border-violet-200 bg-violet-100 text-violet-800",
  "border-rose-200 bg-rose-100 text-rose-800",
];

export const SUBTITLE_BATCH_SIZE = 100;

export const emptyTagDraft: TagDraft = {
  tagName: "",
  tagNameIdn: "",
};

export function formatCount(value: number): string {
  return numberFormatter.format(value);
}

export function cleanInput(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

export function getTagColorClass(tagId: string): string {
  let hash = 0;
  for (const character of tagId) {
    hash += character.charCodeAt(0);
  }

  const colorClass = tagColorStyles[hash % tagColorStyles.length];
  if (colorClass === undefined) {
    return tagColorStyles[0];
  }

  return colorClass;
}

function readNumberField(
  record: Record<string, unknown>,
  fieldName: keyof SubtitleBatchResult,
): number | null {
  const value = record[fieldName];
  if (typeof value !== "number") {
    return null;
  }
  return value;
}

export function parseSubtitleBatchResult(
  rawResult: unknown,
): SubtitleBatchResult | null {
  if (typeof rawResult !== "object" || rawResult === null) {
    return null;
  }

  const record = rawResult as Record<string, unknown>;
  const processed = readNumberField(record, "processed");
  const success = readNumberField(record, "success");
  const errors = readNumberField(record, "errors");
  const totalRemaining = readNumberField(record, "totalRemaining");
  const hasMore = record["hasMore"];

  if (
    processed === null ||
    success === null ||
    errors === null ||
    totalRemaining === null ||
    typeof hasMore !== "boolean"
  ) {
    return null;
  }

  return {
    processed,
    success,
    errors,
    totalRemaining,
    hasMore,
  };
}

export function getQueueTotal(queue: SubtitleQueueSummary | undefined): number {
  if (queue === undefined) {
    return 0;
  }
  return queue.total;
}

export function getBatchConfirmationMessage(
  replaceExisting: boolean,
  currentBatch: number,
): string {
  if (replaceExisting) {
    return `Regenerate subtitles for batch ${currentBatch}? Existing subtitles in this batch will be replaced.`;
  }
  return `Generate subtitles for batch ${currentBatch}? Properties that already have subtitles will be skipped.`;
}

export function describeRejectedAction(error: unknown): string {
  if (error instanceof Error && error.message.length > 0) {
    return error.message;
  }
  return "The request failed without a readable error message.";
}

export function validateTagDraft(tagDraft: TagDraft): string | null {
  if (cleanInput(tagDraft.tagName).length === 0) {
    return "Add a tag name before saving.";
  }
  return null;
}

export function getTagMutationToast(result: TagMutationResult): string {
  if (result.ok === false) {
    return result.error.message;
  }

  if (result.data.status === "existing") {
    return "That property tag already exists.";
  }

  if (result.data.status === "restored") {
    return "Property tag restored.";
  }

  if (result.data.status === "updated") {
    return "Property tag updated.";
  }

  if (result.data.status === "archived") {
    return "Property tag archived.";
  }

  return "Property tag created.";
}
